import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Form from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { applicationService } from '@/projects/admin/services/application-service';
import { IPermission } from '@/core/interfaces/admin/usestate-interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import FormWrapper from '@/core/wrappers/form-wrapper';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import CustomStore from 'devextreme/data/custom_store';
import { t } from 'i18next';
import { ROUTER_CONSTANTS } from '@/core/constants/common/router-constants';


function PermissionDefinition() {

    const actionOptions = [
        { id: "Create", name: t("create") },
        { id: "Update", name: t("update") },
        { id: "Delete", name: t("delete") },
        { id: "Read", name: t("read") },
        { id: "List", name: t("list") }]

    const params = useParams();
    const navigate = useNavigate();
    const formRef: any = useRef<Form>();
    const [permission, setPermission] = useState<IPermission>({ id: '', name: '', permissionCode: '', subject: '', description: '', valueTypeId: null, applicationId: null, valueConfig: null });
    const [valueConfigEditor, setValueConfigEditor] = useState<any>({})




    const applicationStore = useMemo(() => {
        if (!permission) return null;

        return new CustomStore({
            key: 'id',
            load: applicationService.lookupApplication,
            byKey: async (key, loadOptions: any) => {
                let returnObject = null;
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                    const objectsByID = (await applicationService.lookupApplication(loadOptions))
                    returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                }
                return returnObject
            },
            useDefaultSearch: true
        });
    }, [permission]);

    const valueTypeStore = useMemo(() => {

        return new CustomStore({
            key: 'id',
            load: applicationService.getPermissionValueTypes,
            byKey: async (key, loadOptions: any) => {
                let returnObject = null;
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                    const objectsByID = (await applicationService.getPermissionValueTypes(loadOptions))
                    returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                }
                return returnObject
            },
            useDefaultSearch: true
        });
    }, [permission]);

    useEffect(() => {
        if (params.id) {

            applicationService.getPermission(params.id).then((result) => {
                const data = result.data;
                var valueConfig = valueConfigCalculate(data.valueType, data.valueConfig)
                setPermission({ id: data.id, name: data.name, permissionCode: data.permissionCode, subject: data.subject, description: data.description, valueTypeId: data.valueTypeId, valueConfig: valueConfig, applicationId: data.applicationId })
            })
        }
    }, []);

    const navigateBack = async () => {
        navigate('/admin/' + ROUTER_CONSTANTS.ADMIN.Permissions, {})
    }

    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const formData = formRef.current.props.formData;
                formData.valueConfig = JSON.stringify({ options: formData.valueConfig });
                if (params.id) {
                    await applicationService.updatePermission(null, formData);
                } else {
                    await applicationService.createPermission(formData);
                }
                resolve();
                navigateBack();
            } catch (error) {
                reject(error)
            }

        })
    }, []);

    const valueConfigCalculate = (valueType: any, rawValue: any) => {
        if (valueType === "Actions") {
            setValueConfigEditor({
                dataField: 'valueConfig',
                editorType: 'dxTagBox',
                editorOptions: actionOptionEditorOptions,
                label: 'value_config',
                isRequired: true,
            });
          
        } else if (valueType === "Number") {
            setValueConfigEditor({
                dataField: 'valueConfig',
                editorType: 'dxNumberBox',
                label: 'value_config',
            });
        } else if (valueType === "String") {
            setValueConfigEditor({
                dataField: 'valueConfig',
                editorType: 'dxTextBox',
                label: 'value_config',
            });
        }

        return rawValue ? JSON.parse(rawValue).options : rawValue
    }

    const applicationEditorOptions = {
        dataSource: applicationStore,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        showClearButton: true,
    };


    const lastSelectedId = useRef<string | null>(null);
    const selectedValueType = useRef<any | null>(null);
    const valueTypeEditorOptions = {
        dataSource: valueTypeStore,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        showClearButton: true, 
        onSelectionChanged: (e: any) => {
            const currentId = e.selectedItem?.id ?? null;
            if (currentId === lastSelectedId.current) return;
            lastSelectedId.current = currentId;
            selectedValueType.current = e.selectedItem;
            valueConfigCalculate(e.selectedItem.name, null)
            if (permission.valueTypeId !== currentId)
                permission.valueConfig = JSON.parse(e.selectedItem.defaultValue).value
        }
    };

    const actionOptionEditorOptions = {
        dataSource: actionOptions,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        showClearButton: true,

    };

    return (
        <>
            <FormWrapper
                onSubmit={handleSubmit}
                formRef={formRef}
                formData={permission}
                backButtonNavigationPath={'/admin/' + ROUTER_CONSTANTS.ADMIN.Permissions}
                isAddBackBtn={true}

                buttonGroups=
                {[
                    { buttonOptions: BUTTON_CONSTANTS.SaveBtn }
                ]}
                simpleItemGroups={[
                    {
                        caption: permission.id == '' ? t('new_permission') : t('edit_permission'),
                        useColCountByScreen: true,
                        items: [
                            {
                                dataField: 'name',
                                editorType: 'dxTextBox',
                                label: 'name',
                                isRequired: true,

                            },
                            {
                                dataField: 'permissionCode',
                                editorType: 'dxTextBox',
                                label: 'permission_code',
                                isRequired: true,

                            },
                            {
                                dataField: 'subject',
                                editorType: 'dxTextBox',
                                label: 'subject',
                                isRequired: true,

                            },
                            {
                                dataField: 'description',
                                editorType: 'dxTextBox',
                                label: 'description',
                                isRequired: false,

                            },
                            {
                                dataField: 'valueTypeId',
                                editorType: 'dxSelectBox',
                                editorOptions: valueTypeEditorOptions,
                                label: 'value_type',
                                isRequired: true,
                            },
                            {
                                dataField: 'applicationId',
                                editorType: 'dxSelectBox',
                                editorOptions: applicationEditorOptions,
                                label: 'application',
                            },
                            valueConfigEditor,

                        ],

                    }
                ]}
            />
        </>
    );
}
export default PermissionDefinition;